/*
 * Pfanner — Pfand (deposit) as real cart line items.
 *
 * The Cart-Transform Function that was going to add the deposit at checkout is
 * blocked (Functions from a custom-distribution app need Shopify Plus; this store
 * is not on Plus — see docs/plans/2026-08-05-pfand-plus-blocker.md). Instead the
 * deposit is charged as a genuine, priced line item that this reconciler keeps in
 * sync with the deposit-bearing beverages in the cart. Modeled 1:1 on
 * assets/pfanner-gift.js — same cart-mechanics, same loop guards.
 *
 * HOW IT KNOWS WHAT THE DEPOSIT SHOULD BE
 *   /cart.js exposes only variant_id + quantity + key — NOT the per-variant
 *   metafields (deposit class, units_per_pack) the deposit depends on. So Liquid
 *   (which CAN read metafields) pre-computes, per deposit-bearing beverage line, a
 *   target { deposit-variant-id, quantity } and renders it as JSON in
 *   #pfanner-pfand-targets (snippets/pfand-targets.liquid). Those targets are a
 *   pure function of the NON-deposit lines, so adding/removing deposit lines can
 *   never change them — that is the primary loop guard (the analogue of the gift's
 *   "basis excludes the gift"). The targets element lives inside the cart sections
 *   Dawn re-renders on every cart change, so it is always fresh when we run.
 *
 * WHAT IT DOES each cart change: read the desired {deposit-variant -> total qty}
 * from the targets, read the deposit lines actually in the cart, and add / change /
 * remove deposit lines until the two agree.
 *
 * TAX: each deposit variant carries the VAT rule in its own `taxable` flag
 * (AT·Einweg is a durchlaufender Posten at 0 %, everything else follows the
 * drink's rate). Nothing here computes tax — Shopify does, from the variant.
 */
(function () {
  'use strict';

  var CONFIG_ID = 'pfanner-pfand-config';
  var reconciling = false; // true while an add/change/remove chain is in flight (guards re-entrancy)
  var failed = {}; // variant id -> true once its add returned non-OK; don't hammer it this session
  var debounceTimer = null;

  // Normalise any variant id to its trailing digits: numeric, string, or GID all
  // reduce to the bare number. /cart.js returns numeric variant_id; our config and
  // targets may hold GIDs — both sides MUST be normalised before comparing, and the
  // id must be bare digits before it is POSTed to /cart/add.
  function numId(v) {
    var m = String(v).match(/\d+$/);
    return m ? m[0] : '';
  }

  // Config lives on the persistent #pfanner-pfand-config holder (present even when
  // the cart is empty), so the reconciler can subscribe on every page load. It
  // carries the SET of all Pfand variant ids (both markets, all classes) so a cart
  // line can be recognised as a deposit line.
  function getConfig() {
    var el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    if (el.dataset.pfandEnabled === 'false') return null;
    var ids = String(el.dataset.pfandVariantIds || '')
      .split(',')
      .map(function (s) { return numId(s); })
      .filter(Boolean);
    if (!ids.length) return null;
    var set = {};
    ids.forEach(function (id) { set[id] = true; });
    return { depositIds: set };
  }

  // Desired deposit lines: sum the server-computed targets into { variantId -> qty }.
  // Read from the cart-page holder if present (fresh on /cart), else the drawer's.
  function readDesired() {
    var el = document.getElementById('pfanner-pfand-targets') ||
             document.getElementById('pfanner-pfand-targets-drawer');
    var out = {};
    if (!el) return out;
    var list;
    try { list = JSON.parse(el.textContent || '[]'); } catch (e) { return out; }
    if (!Array.isArray(list)) return out;
    list.forEach(function (t) {
      var id = numId(t && t.id);
      var qty = parseInt(t && t.qty, 10);
      if (!id || !(qty > 0)) return;
      out[id] = (out[id] || 0) + qty;
    });
    return out;
  }

  // Deposit lines actually in the cart, by variant id -> { qty, key }.
  function findDepositLines(cart, depositIds) {
    var out = {};
    (cart.items || []).forEach(function (item) {
      var id = numId(item.variant_id);
      if (depositIds[id]) out[id] = { qty: item.quantity, key: item.key };
    });
    return out;
  }

  function fetchCart() {
    return fetch((window.routes ? window.routes.cart_url : '/cart') + '.js', {
      headers: { Accept: 'application/json' },
    }).then(function (r) { return r.json(); });
  }

  // Sections we can render without opening the drawer (mirrors what Dawn touches on
  // a cart update) — re-rendering these also refreshes the deposit targets they hold.
  function sectionsToRequest() {
    var list = ['cart-drawer', 'cart-icon-bubble'];
    var main = document.getElementById('main-cart-items');
    if (main && main.dataset.id) list.push(main.dataset.id);
    return list;
  }

  function renderSections(parsed) {
    var sections = (parsed && parsed.sections) || {};

    if (sections['cart-drawer']) {
      var ddoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      ['cart-drawer-items', '.cart-drawer__footer'].forEach(function (sel) {
        var src = ddoc.querySelector(sel);
        var tgt = document.querySelector(sel);
        if (src && tgt) tgt.replaceWith(src);
      });
      // Those two selectors carry the FOOTER's empty-state switch but not the
      // drawer's own, and they miss the sibling `.drawer__inner-empty` block —
      // so a render that crosses the empty <-> not-empty line would leave
      // "Warenkorb ist leer" standing above a real total. Helper + full note in
      // assets/cart.js (loaded first by snippets/cart-drawer.liquid).
      if (typeof window.pfannerSyncCartDrawerEmptyState === 'function') {
        window.pfannerSyncCartDrawerEmptyState(ddoc);
      }
    }

    var main = document.getElementById('main-cart-items');
    if (main && main.dataset.id && sections[main.dataset.id]) {
      var mdoc = new DOMParser().parseFromString(sections[main.dataset.id], 'text/html');
      var src = mdoc.querySelector('cart-items');
      var tgt = document.querySelector('cart-items');
      if (src && tgt) tgt.innerHTML = src.innerHTML;
    }

    if (sections['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        var bdoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        var inner = bdoc.querySelector('.shopify-section') || bdoc.body;
        bubble.innerHTML = inner.innerHTML;
      }
    }
  }

  function refreshBubbleOnly() {
    return fetch((window.routes ? window.routes.cart_url : '/cart') + '?section_id=cart-icon-bubble')
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var bubble = document.getElementById('cart-icon-bubble');
        if (!bubble) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var inner = doc.querySelector('.shopify-section') || doc.body;
        bubble.innerHTML = inner.innerHTML;
      })
      .catch(function () {});
  }

  // One cart mutation. `withSections` is set only on the LAST op of a chain, so the
  // cart UI is re-rendered exactly once per reconcile.
  function mutate(op, withSections) {
    var url, body;
    if (op.kind === 'add') {
      url = (window.routes ? window.routes.cart_add_url : '/cart/add');
      body = { items: [{ id: op.id, quantity: op.qty }] };
    } else {
      url = (window.routes ? window.routes.cart_change_url : '/cart/change');
      body = { id: op.id, quantity: op.qty };
    }
    if (withSections) {
      body.sections = sectionsToRequest();
      body.sections_url = window.location.pathname;
    }
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) { return r.json(); });
  }

  // Run ops sequentially (never in parallel — concurrent /cart writes race).
  function runOps(ops) {
    var chain = Promise.resolve(null);
    ops.forEach(function (op, i) {
      var last = i === ops.length - 1;
      chain = chain.then(function () {
        return mutate(op, last).then(function (state) {
          var ok = state && !(state.status && state.status >= 400);
          if (op.kind === 'add' && !ok) failed[op.id] = true;
          if (last) {
            if (state && state.sections) renderSections(state);
            else fetchCart().then(refreshBubbleOnly);
            // Deposit lines change the cart total. Widgets outside the cart UI (the
            // PDP's free-shipping bar) listen for this; a cartUpdate publish would
            // re-trigger our own subscriber instead.
            document.dispatchEvent(new CustomEvent('pfanner:cart-synced'));
          }
          return state;
        });
      });
    });
    return chain;
  }

  function evaluate() {
    var cfg = getConfig();
    if (!cfg || reconciling) return;

    // G1: claim the critical section SYNCHRONOUSLY, before the async fetchCart, so a
    // second cartUpdate in the network gap cannot double-add. Cleared in the finally.
    reconciling = true;

    fetchCart()
      .then(function (cart) {
        if (!cart) return;
        var desired = readDesired();
        var actual = findDepositLines(cart, cfg.depositIds);

        // Build the minimal op set. Because `desired` is derived only from the
        // non-deposit lines, reconciling deposit lines cannot change it → no loop.
        var ids = {};
        Object.keys(desired).forEach(function (id) { ids[id] = true; });
        Object.keys(actual).forEach(function (id) { ids[id] = true; });

        var ops = [];
        Object.keys(ids).forEach(function (id) {
          var want = desired[id] || 0;
          var have = actual[id];
          if (want > 0 && !have) {
            if (!failed[id]) ops.push({ kind: 'add', id: id, qty: want });
          } else if (want === 0 && have) {
            ops.push({ kind: 'change', id: have.key, qty: 0 });
          } else if (want > 0 && have && have.qty !== want) {
            ops.push({ kind: 'change', id: have.key, qty: want });
          }
        });

        if (!ops.length) return; // already in sync
        return runOps(ops);
      })
      .catch(function (e) {
        if (window.console) console.warn('[pfanner-pfand]', e);
      })
      .finally(function () {
        reconciling = false;
      });
  }

  function scheduleEvaluate() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(evaluate, 120);
  }

  function init() {
    if (!getConfig()) return; // no config / disabled → stay inert

    // React to every cart change. We render sections directly and deliberately do
    // NOT publish cartUpdate, so our own mutations don't re-trigger this subscriber;
    // the reconciling flag guards the rest.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { scheduleEvaluate(); });
    }

    // Reconcile once on load (the cart may already hold beverages, or a stale/
    // tampered deposit line).
    scheduleEvaluate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
